import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Camera, Check, X } from 'lucide-react';
import { useCourse } from '@/hooks/useCourse';
import { useTelegram } from '@/hooks/useTelegram';
import { Mascot } from '@/components/Mascot';

// ════════════════════════════════════════════════════════════════
// Точка А (день 1) — тёплый чек-ин + ОПЦИОНАЛЬНОЕ фото лица.
// Не принуждаем: предлагаем и кратко объясняем зачем (отслеживать динамику).
// ════════════════════════════════════════════════════════════════

const MOODS = [
  { v: 'тяжело', e: '😞' }, { v: 'обычно', e: '😐' }, { v: 'неплохо', e: '🙂' }, { v: 'отлично', e: '😊' },
];
const ENERGY = [
  { v: 'на нуле', e: '🔋' }, { v: 'средне', e: '⚡' }, { v: 'бодро', e: '🔥' },
];

export default function PointAScreen() {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const { startCourse, uploadProgressPhoto } = useCourse();

  const [mood, setMood] = useState<string | null>(null);
  const [energy, setEnergy] = useState<string | null>(null);
  const [words, setWords] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  function pickPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
    haptic('light');
  }

  async function begin() {
    setSaving(true);
    haptic('medium');
    try {
      let photoPath: string | null = null;
      if (photoFile) {
        photoPath = await uploadProgressPhoto(photoFile, 'a');
      }
      const pointA = {
        mood, energy,
        words: words.trim() || null,
        at: new Date().toISOString(),
      };
      await startCourse(pointA, photoPath);
      haptic('success');
      nav('/path');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="px-5 pt-6 pb-28 max-w-md mx-auto">
      <button onClick={() => nav('/path')} className="w-9 h-9 -ml-2 rounded-full flex items-center justify-center text-ink-dim active:bg-bg-elevated">
        <ChevronLeft size={20} />
      </button>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4 space-y-5">
        <div>
          <div className="text-3xl mb-2">🧭</div>
          <h1 className="font-display text-2xl font-bold leading-tight">С чего ты начинаешь</h1>
          <p className="text-sm text-ink-dim mt-1 leading-relaxed">
            Отметим точку А — чтобы через 21 день было с чем сравнить. Это для тебя, не для оценки.
          </p>
        </div>

        {/* Настроение */}
        <div>
          <div className="text-xs text-ink-dim mb-1.5">Как ты сейчас?</div>
          <div className="grid grid-cols-4 gap-1.5">
            {MOODS.map((m) => (
              <button key={m.v} onClick={() => { haptic('light'); setMood(m.v); }}
                className={['py-2 rounded-lg border text-xs transition-all', mood === m.v ? 'bg-emerald-glow/15 border-emerald-glow/50' : 'bg-bg-card border-line text-ink-muted'].join(' ')}>
                <div className="text-lg leading-none mb-0.5">{m.e}</div>{m.v}
              </button>
            ))}
          </div>
        </div>

        {/* Энергия */}
        <div>
          <div className="text-xs text-ink-dim mb-1.5">Энергия</div>
          <div className="grid grid-cols-3 gap-1.5">
            {ENERGY.map((m) => (
              <button key={m.v} onClick={() => { haptic('light'); setEnergy(m.v); }}
                className={['py-2 rounded-lg border text-xs transition-all', energy === m.v ? 'bg-emerald-glow/15 border-emerald-glow/50' : 'bg-bg-card border-line text-ink-muted'].join(' ')}>
                <span className="mr-1">{m.e}</span>{m.v}
              </button>
            ))}
          </div>
        </div>

        {/* Слова (опц.) */}
        <div>
          <div className="text-xs text-ink-dim mb-1.5">В двух словах — как тебе сейчас? <span className="text-ink-faint">(по желанию)</span></div>
          <textarea value={words} onChange={(e) => setWords(e.target.value)} maxLength={200} rows={2}
            placeholder="напр. «ем на автомате, устаю к вечеру»"
            className="input text-sm w-full resize-none" />
        </div>

        {/* Приглашение на фото (не принуждаем) */}
        <div className="rounded-2xl border border-line bg-bg-elevated/40 p-3.5">
          <div className="flex gap-2.5 items-start">
            <Mascot pose="calm" compact float={false} className="w-8 h-8 shrink-0" />
            <div className="text-sm leading-relaxed">
              <b>Хочешь маленький эксперимент — только для себя?</b><br />
              За 21 день у многих меняется лицо — уходят отёки, взгляд живее; на весах это не видно, а на фото заметно.
              Снимок останется <b>только у тебя, без оценок</b> — чтобы отслеживать свою динамику.
            </div>
          </div>

          {!photoPreview ? (
            <button onClick={() => fileRef.current?.click()}
              className="w-full mt-3 py-2.5 rounded-xl bg-bg-card border border-emerald-glow/30 text-emerald-glow text-sm font-medium flex items-center justify-center gap-1.5 active:scale-[0.98]">
              <Camera size={15} /> Сделать фото
            </button>
          ) : (
            <div className="mt-3 flex items-center gap-3">
              <img src={photoPreview} alt="" className="w-14 h-14 rounded-xl object-cover border border-line" />
              <div className="text-xs text-ink-dim flex-1">Фото добавлено 🔒 приватно</div>
              <button onClick={() => { setPhotoFile(null); setPhotoPreview(null); }} className="text-ink-dim p-1"><X size={16} /></button>
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" capture="user" onChange={pickPhoto} className="hidden" />
          <p className="text-[10px] text-ink-faint mt-2">🔒 Приватно. Удалить можно в любой момент. Пропустить — это ок.</p>
        </div>

        <button onClick={begin} disabled={saving}
          className="w-full py-3.5 rounded-xl bg-emerald-glow text-bg font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] disabled:opacity-50">
          {saving ? 'Начинаем…' : <>Начать Путь <Check size={16} /></>}
        </button>
      </motion.div>
    </div>
  );
}
